import { useMemo } from 'react';
import { useAuth } from '../../../../../authContext';
import { useSecurity } from '../../../../../SecurityContext';
import { linkedHandlesIncludeMaster } from './dashboardUtils';
import { normalizeRosterStr } from '../../rosterTools/utils/rosterHelpers';

export function useDashboardHistoryAccess(player, { subjectHandles = null } = {}) {
  const { isLoggedIn, userId, handles, discordName } = useAuth();
  const {
    userHasPermission,
    isViewAsActive,
    viewAsUserId,
    subjectHasPermission,
  } = useSecurity();

  const canViewHistory = useMemo(() => {
    if (!isLoggedIn) return false;
    if (userHasPermission('private.navbar.roster.file.history.bypass', 'read')) {
      return true;
    }

    if (isViewAsActive) {
      return subjectHasPermission('private.navbar.personalhub.history', 'read');
    }

    const handleList = Array.isArray(handles) ? handles : [];
    if (linkedHandlesIncludeMaster(handleList)) {
      return true;
    }

    const effectiveUserId = userId;
    const puid = player?.userId;
    if (puid != null && effectiveUserId != null && Number(puid) === Number(effectiveUserId)) {
      return true;
    }

    const playerHandleId = Number(player?.handleId ?? player?.id);
    const linkedIds = new Set(
      handleList
        .filter((h) => h && h.linkedToUser)
        .map((h) => Number(h.handleId ?? h.id))
        .filter((n) => Number.isInteger(n) && n > 0),
    );
    if (playerHandleId > 0 && linkedIds.has(playerHandleId)) {
      return true;
    }

    const rosterHandle = normalizeRosterStr(player?.rosterHandle);
    if (rosterHandle) {
      const match = handleList.some(
        (h) => h?.linkedToUser && normalizeRosterStr(h?.handle) === rosterHandle,
      );
      if (match) return true;
    }

    const rosterDiscord = normalizeRosterStr(player?.rosterDiscordName);
    const authDiscord = normalizeRosterStr(discordName);
    if (rosterDiscord && authDiscord && rosterDiscord === authDiscord) {
      return true;
    }

    if (Array.isArray(subjectHandles) && linkedHandlesIncludeMaster(subjectHandles, { isViewAsActive: true })) {
      return true;
    }

    return false;
  }, [
    discordName,
    handles,
    isLoggedIn,
    isViewAsActive,
    player,
    subjectHandles,
    subjectHasPermission,
    userHasPermission,
    userId,
  ]);

  return { canViewHistory };
}
